import { c as _c } from "react/compiler-runtime";
import { PaginationBar } from '@systemeio/components';
import React, { RefObject, useEffect, useState } from 'react';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { DataWithPaginationInterface } from 'shared/types/pagination-interface';
import { LimitType, OrderEnum, PaginationInterface } from '../../../types/pagination-interface';
import { resolveRowId } from '../utils';
export interface PaginationProps<T> {
  data?: DataWithPaginationInterface<T>;
  limiter: {
    limit: LimitType;
    setLimit: React.Dispatch<React.SetStateAction<LimitType>>;
  };
  paginator: {
    pagination: PaginationInterface;
    setPagination: React.Dispatch<React.SetStateAction<PaginationInterface>>;
  };
  tableRef?: RefObject<HTMLDivElement | null>;
  getRowId?: (row: T) => string | number;
}
export const Pagination = t0 => {
  const $ = _c(37);
  const {
    data,
    limiter,
    paginator,
    tableRef,
    getRowId
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const {
    pagination,
    setPagination
  } = paginator;
  const {
    limit,
    setLimit
  } = limiter;
  let t1;
  if ($[0] !== tableRef) {
    t1 = () => {
      if (tableRef?.current) {
        tableRef.current.scrollIntoView({
          behavior: "smooth"
        });
      }
    };
    $[0] = tableRef;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const onPaginationClick = t1;
  const [isPaginationShown, setIsPaginationShown] = useState(true);
  const [isFirst, setIsFirst] = useState(true);
  const [isLast, setIsLast] = useState(false);
  let t2;
  if ($[2] !== data?.items.length || $[3] !== isFirst || $[4] !== setPagination) {
    t2 = () => {
      if (!isFirst && data?.items.length === 0) {
        setPagination({
          order: OrderEnum.next
        });
      }
    };
    $[2] = data?.items.length;
    $[3] = isFirst;
    $[4] = setPagination;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = data?.items.length;
  let t4;
  if ($[6] !== isFirst || $[7] !== setPagination || $[8] !== t3) {
    t4 = [t3, isFirst, setPagination];
    $[6] = isFirst;
    $[7] = setPagination;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  useEffect(t2, t4);
  let t5;
  let t6;
  if ($[10] !== data || $[11] !== pagination.order || $[12] !== pagination.startFromId) {
    t5 = () => {
      if (data) {
        setIsFirst(true);
        setIsLast(false);
        bb32: switch (pagination.order) {
          case "next":
            {
              if (pagination.startFromId) {
                setIsFirst(false);
              }
              if (!data.hasMore) {
                setIsLast(true);
              }
              break bb32;
            }
          case "prev":
            {
              if (data.hasMore) {
                setIsFirst(false);
              }
            }
        }
      }
    };
    t6 = [data, pagination.order, pagination.startFromId];
    $[10] = data;
    $[11] = pagination.order;
    $[12] = pagination.startFromId;
    $[13] = t5;
    $[14] = t6;
  } else {
    t5 = $[13];
    t6 = $[14];
  }
  useEffect(t5, t6);
  let t7;
  if ($[15] !== data || $[16] !== isFirst || $[17] !== limiter.limit || $[18] !== pagination.order) {
    t7 = () => {
      if (data) {
        setIsPaginationShown(true);
        if (!isFirst || limiter.limit !== 10) {
          return;
        }
        if (pagination.order === "next" && !data.hasMore) {
          setIsPaginationShown(false);
        }
        if (data.items.length === 0) {
          setIsPaginationShown(false);
        }
      } else {
        setIsPaginationShown(false);
      }
    };
    $[15] = data;
    $[16] = isFirst;
    $[17] = limiter.limit;
    $[18] = pagination.order;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== data || $[21] !== isFirst || $[22] !== limiter.limit || $[23] !== pagination.order || $[24] !== pagination.startFromId) {
    t8 = [data, isFirst, limiter.limit, pagination.order, pagination.startFromId];
    $[20] = data;
    $[21] = isFirst;
    $[22] = limiter.limit;
    $[23] = pagination.order;
    $[24] = pagination.startFromId;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  useEffect(t7, t8);
  let t9;
  if ($[26] !== data || $[27] !== getRowId || $[28] !== isFirst || $[29] !== isLast || $[30] !== isPaginationShown || $[31] !== limit || $[32] !== onPaginationClick || $[33] !== setLimit || $[34] !== setPagination || $[35] !== t) {
    t9 = isPaginationShown ? <PaginationBar hasNextPage={!isLast} hasPreviousPage={!isFirst} limit={limit} onLimitChange={nextLimit => {
      setLimit(nextLimit as LimitType);
      setPagination({
        startFromId: undefined,
        order: OrderEnum.next
      });
      onPaginationClick();
    }} onPreviousPage={() => {
      if (!data?.items || data.items.length === 0) {
        return;
      }
      const firstItem = data.items[0];
      if (!firstItem) {
        return;
      }
      const startFromId = resolveRowId({
        row: firstItem,
        getRowId
      });
      setPagination({
        startFromId,
        order: OrderEnum.prev
      });
      onPaginationClick();
    }} onNextPage={() => {
      if (!data?.items || data.items.length === 0) {
        return;
      }
      const lastItem = data.items[data.items.length - 1];
      if (!lastItem) {
        return;
      }
      const startFromId_0 = resolveRowId({
        row: lastItem,
        getRowId
      });
      setPagination({
        startFromId: startFromId_0,
        order: OrderEnum.next
      });
      onPaginationClick();
    }} rootProps={{
      "data-test": "table-pagination"
    }} selectProps={{
      "aria-label": t("global.limit")
    }} prevButtonProps={{
      "aria-label": t("global.previous")
    }} nextButtonProps={{
      "aria-label": t("global.next")
    }} /> : null;
    $[26] = data;
    $[27] = getRowId;
    $[28] = isFirst;
    $[29] = isLast;
    $[30] = isPaginationShown;
    $[31] = limit;
    $[32] = onPaginationClick;
    $[33] = setLimit;
    $[34] = setPagination;
    $[35] = t;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  return t9;
};